import React, { useState } from 'react';
import { 
  DocumentTextIcon, 
  EyeIcon, 
  ClipboardDocumentIcon,
  InformationCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline';
import { ScissorsIcon } from '@heroicons/react/24/outline';
import ClassificationBadge from './ClassificationBadge';
import AuthorityReviewFlag from './AuthorityReviewFlag';
import SCGCitation from './SCGCitation';
import { useNavigate } from 'react-router-dom';

const PortionMarkedDocument = ({ paragraphResult, formattedDocumentMutation }) => {
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState('document'); // 'document' or 'reasoning'
  const [expandedParagraphs, setExpandedParagraphs] = useState(new Set());

  // Function to replace SCG rule IDs with SCGCitation components
  const renderScgRulesInText = (text) => {
    if (!text || typeof text !== 'string') return text;
    
    // Regex to match SCG rule patterns: SCG-XXX, SCG-UNK-XXXXXX-XXX, etc.
    const scgRulePattern = /(SCG-[A-Z0-9-]+)/g;
    
    const parts = text.split(scgRulePattern);
    
    return parts.map((part, index) => {
      if (scgRulePattern.test(part)) {
        return (
          <SCGCitation 
            key={index} 
            ruleId={part} 
            compact={true} 
          />
        );
      }
      return part;
    });
  };


  const handleTearlineClick = () => {
    // Navigate to tearline page with the classified document
    navigate('/tearline', { 
      state: { 
        classifiedDocument: paragraphResult.portion_marked_document,
        documentClassification: paragraphResult.document_classification,
        fullClassification: paragraphResult.full_classification
      } 
    });
  };


  const toggleParagraphExpansion = (paragraphNumber) => {
    const newExpanded = new Set(expandedParagraphs);
    if (newExpanded.has(paragraphNumber)) {
      newExpanded.delete(paragraphNumber);
    } else {
      newExpanded.add(paragraphNumber);
    }
    setExpandedParagraphs(newExpanded);
  };

  const expandAllParagraphs = () => {
    const allParagraphs = new Set(paragraphResult.reasoning?.map(r => r.paragraph_number) || []);
    setExpandedParagraphs(allParagraphs);
  };

  const collapseAllParagraphs = () => {
    setExpandedParagraphs(new Set());
  };

  return (
    <div className="bg-white rounded-lg shadow-lg border border-gray-200">
      {/* Header with Overall Classification */}
      <div className="bg-blue-50 border-b border-blue-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <DocumentTextIcon className="h-6 w-6 text-blue-600" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Classified Document
              </h3>
              <div className="flex items-center space-x-3 mt-1">
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-600">Overall Classification:</span>
                  <ClassificationBadge 
                    classification={paragraphResult.document_classification}
                    fullClassification={paragraphResult.full_classification}
                    caveats={paragraphResult.caveats}
                    releasability={paragraphResult.releasability}
                    confidence={paragraphResult.document_confidence}
                  />
                  <AuthorityReviewFlag 
                    reasoning={paragraphResult.reasoning} 
                    contradictions={paragraphResult.contradictions}
                    variant="compact" 
                  />
                </div>
                <button
                  onClick={handleTearlineClick}
                  className="inline-flex items-center space-x-1 px-3 py-1 bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors text-sm"
                  title="Generate Tearline Sanitization"
                >
                  <ScissorsIcon className="w-4 h-4" />
                  <span>Tearline</span>
                </button>
              </div>
            </div>
          </div>
          <div className="text-sm text-gray-500">
            {paragraphResult.total_paragraphs} paragraph{paragraphResult.total_paragraphs !== 1 ? 's' : ''}
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8 px-6" aria-label="Tabs">
          <button
            onClick={() => setActiveView('document')}
            className={`${
              activeView === 'document'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2`}
          >
            <DocumentTextIcon className="h-4 w-4" />
            <span>Portion-Marked Document</span>
          </button>
          <button
            onClick={() => setActiveView('reasoning')}
            className={`${
              activeView === 'reasoning'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2`}
          >
            <InformationCircleIcon className="h-4 w-4" />
            <span>Classification Reasoning</span>
          </button>
        </nav>
      </div>

      {/* Document View */}
      {activeView === 'document' && (
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-medium text-gray-900">
              Ready-to-Use Classified Document
            </h4>
            <button
              onClick={() => {
                // Get the last uploaded file from the file input
                const fileInput = document.querySelector('input[type="file"]');
                if (fileInput && fileInput.files[0] && paragraphResult) {
                  // Prepare classification data from existing results
                  // Use the same clean classification logic as the UI badge
                  // Import the function from ClassificationBadge (inline for now)
                  const extractCleanClassification = (fullClassification, baseClassification, caveats = [], releasability = []) => {
                    if (!fullClassification || fullClassification === baseClassification) {
                      const parts = [baseClassification.toUpperCase()];
                      if (caveats && caveats.length > 0) {
                        parts.push(...caveats);
                      }
                      if (releasability && releasability.length > 0) {
                        parts.push(...releasability);
                      }
                      return parts.join('//');
                    }
                    
                    let cleaned = fullClassification.toUpperCase();
                    const classificationLevels = ['TOP SECRET', 'SECRET', 'CONFIDENTIAL', 'UNCLASSIFIED'];
                    let baseLevel = 'UNKNOWN';
                    
                    for (const level of classificationLevels) {
                      if (cleaned.includes(level)) {
                        baseLevel = level;
                        break;
                      }
                    }
                    
                    const parts = [baseLevel];
                    const compartments = ['SI', 'HCS', 'TK', 'GAMMA', 'ORCON'];
                    for (const comp of compartments) {
                      if (cleaned.includes('//' + comp + '//') || cleaned.includes(' ' + comp + ' ') || cleaned.includes('//' + comp)) {
                        if (!parts.includes(comp)) {
                          parts.push(comp);
                        }
                      }
                    }
                    
                    const releasabilityMarkings = ['NOFORN', 'ORCON', 'FOUO', 'LIMDIS'];
                    for (const rel of releasabilityMarkings) {
                      if (cleaned.includes(rel)) {
                        if (!parts.includes(rel)) {
                          parts.push(rel);
                        }
                      }
                    }
                    
                    return parts.join('//');
                  };
                  
                  // Get clean classification using the same logic as the UI
                  const fullClassification = extractCleanClassification(
                    paragraphResult.full_classification,
                    paragraphResult.document_classification || 'UNCLASSIFIED',
                    paragraphResult.caveats || [],
                    paragraphResult.releasability || []
                  );
                  
                  const classificationData = {
                    document_classification: fullClassification,
                    paragraph_results: paragraphResult.paragraphs || paragraphResult.reasoning || []
                  };
                  
                  formattedDocumentMutation?.mutate({
                    file: fileInput.files[0],
                    classificationResults: classificationData
                  });
                } else {
                  alert('Please upload a file first or wait for classification to complete');
                }
              }}
              disabled={formattedDocumentMutation?.isLoading}
              className="inline-flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ClipboardDocumentIcon className="h-4 w-4" />
              <span>{formattedDocumentMutation?.isLoading ? 'Generating...' : 'Download Formatted'}</span>
            </button>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <pre className="whitespace-pre-wrap font-mono text-sm text-gray-800 leading-relaxed">
              {paragraphResult.portion_marked_document}
            </pre>
          </div>

          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start space-x-2">
              <InformationCircleIcon className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Usage Instructions:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Each paragraph is marked with its appropriate classification level</li>
                  <li>Copy and paste this document directly into your work</li>
                  <li>Portion marks like (TS//SI//NF) indicate classification and controls</li>
                  <li>Review the "Classification Reasoning" tab for detailed justifications</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reasoning View */}
      {activeView === 'reasoning' && (
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-medium text-gray-900">
              Classification Analysis & Reasoning
            </h4>
            <div className="flex space-x-2">
              <button
                onClick={expandAllParagraphs}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Expand All
              </button>
              <span className="text-gray-400">|</span>
              <button
                onClick={collapseAllParagraphs}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Collapse All
              </button>
            </div>
          </div>

          <div className="space-y-4">
            {paragraphResult.reasoning?.map((reasoning, index) => (
              <div key={index} className="border border-gray-200 rounded-lg">
                <button
                  onClick={() => toggleParagraphExpansion(reasoning.paragraph_number)}
                  className="w-full px-4 py-3 text-left bg-gray-50 hover:bg-gray-100 rounded-t-lg flex items-center justify-between"
                >
                  <div className="flex items-center space-x-3">
                    <span className="font-medium text-gray-900">
                      Paragraph {reasoning.paragraph_number}
                    </span>
                    <span className="px-2 py-1 bg-white text-gray-900 border border-gray-400 text-xs rounded font-mono">
                      {reasoning.classification}
                    </span>
                    <span className="text-sm text-gray-600">
                      {Math.round(reasoning.confidence * 100)}% confidence
                    </span>
                    <AuthorityReviewFlag 
                      reasoning={reasoning.reasoning}
                      contradictions={reasoning.contradictions}
                      variant="compact" 
                    />
                  </div>
                  {expandedParagraphs.has(reasoning.paragraph_number) ? (
                    <ChevronUpIcon className="h-5 w-5 text-gray-400" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                  )}
                </button>

                {expandedParagraphs.has(reasoning.paragraph_number) && (
                  <div className="px-4 py-3 border-t border-gray-200">
                    <div className="space-y-3">
                      {/* Text Preview */}
                      <div>
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                          Text Content
                        </label>
                        <p className="mt-1 text-sm text-gray-700 bg-gray-50 p-2 rounded">
                          {reasoning.text_preview}
                        </p>
                      </div>

                      {/* SCG Citations */}
                      {reasoning.scg_citations && reasoning.scg_citations.length > 0 && (
                        <div>
                          <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                            SCG Rule Citations
                          </label>
                          <ul className="mt-1 space-y-1">
                            {reasoning.scg_citations.map((citation, citationIndex) => (
                              <li key={citationIndex} className="text-sm text-blue-700 bg-blue-50 p-2 rounded">
                                {renderScgRulesInText(citation)}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Detailed Reasoning */}
                      <div>
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                          Classification Reasoning
                        </label>
                        <div className="mt-1 text-sm text-gray-700 bg-gray-50 p-2 rounded">
                          <div className="whitespace-pre-wrap font-sans">
                            {renderScgRulesInText(reasoning.reasoning || 'No detailed reasoning available')}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {(!paragraphResult.reasoning || paragraphResult.reasoning.length === 0) && (
            <div className="text-center py-8">
              <InformationCircleIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No reasoning available</h3>
              <p className="mt-1 text-sm text-gray-500">
                Classification reasoning was not provided for this document.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PortionMarkedDocument;